import React, { useRef, useState } from 'react';
import TypeAhead, { type TypeAheadOption } from '../../../components/TypeAhead';
import quoteService from '../../../services/quote.service';
import LookupResultCard from './LookupResultCard';
import type { CustomerLookupRow } from '../fixtures';

interface Props {
  /** Current customer display name (data.customer). */
  value: string;
  /** Bound customercode; > 0 shows the linked note and suppresses search.
   *  0 = an unlinked / new customer — search is enabled. */
  linkedCode: number;
  /** User typed — the caller renames and UNLINKS (customercode → 0). */
  onType: (name: string) => void;
  /** User picked a real customer — the caller links the code + hydrates
   *  market / key account / sales rep onto the quote. */
  onPick: (row: CustomerLookupRow) => void;
  /** When provided, a "+ Add As New Customer" action shows in the dropdown,
   *  called with the current search term (create mode → opens the New Customer modal). */
  onAddNew?: (term: string) => void;
  ariaLabel?: string;
  placeholder?: string;
}

/**
 * Debounced cms.customers typeahead — the customer-card twin of
 * BillToLookupField, built on the shared <TypeAhead> (free-text async mode).
 * A pick links the customercode and hydrates market / key account / sales rep;
 * typing unlinks back to code 0. Lookup only (new customers → New Customer modal).
 */
const CustomerLookupField: React.FC<Props> = ({ value, linkedCode, onType, onPick, onAddNew, ariaLabel, placeholder }) => {
  const [options, setOptions] = useState<TypeAheadOption[]>([]);
  const [searching, setSearching] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seq = useRef(0);

  const search = (term: string) => {
    if (timer.current) clearTimeout(timer.current);
    const q = term.trim();
    if (q.length < 2 || linkedCode > 0) { setOptions([]); setSearching(false); return; }
    // In flight from the FIRST keystroke (debounce included) so the dropdown
    // shows "Searching…" instead of a premature "No results found"; the
    // sequence counter drops out-of-order responses from stale keystrokes.
    const mySeq = ++seq.current;
    setSearching(true);
    timer.current = setTimeout(async () => {
      try {
        const rows = await quoteService.lookupCustomer(q);
        if (mySeq !== seq.current) return;
        setOptions(rows.map((r) => ({
          value: String(r.customercode),
          label: r.custname,
          description: [[r.city, r.state].filter(Boolean).join(', '), r.market, `#${r.customercode}`].filter(Boolean).join('  ·  '),
          data: r,
        })));
      } catch {
        if (mySeq === seq.current) setOptions([]);
      } finally {
        if (mySeq === seq.current) setSearching(false);
      }
    }, 300);
  };

  // While a real customer is linked, show its code inline after the name
  // (e.g. "DSV AIR & SEA (4017)") rather than a separate note line. Typing unlinks
  // (parent sets customercode→0); strip the appended "(code)" so it can't leak
  // into the customer name on the first edit.
  const display = linkedCode > 0 && value ? `${value} (${linkedCode})` : value;
  // Strip a trailing "(code)" — or a partial one left mid-backspace, e.g.
  // "(4017", "(40" — so the appended code never leaks into the typed name. Only
  // a digit-only paren group is stripped, so a real "ACME (WEST)" is preserved.
  const handleType = (text: string) =>
    onType(linkedCode > 0 ? text.replace(/\s*\(\d*\)?$/, '') : text);

  return (
    <div className="flex-1 min-w-0">
      <TypeAhead
        freeText
        compact
        multiple={false}
        value={display}
        options={options}
        onChange={handleType}
        onSearch={search}
        onSelect={(opt) => onPick(opt.data as CustomerLookupRow)}
        onCreateNew={onAddNew}
        createNewLabel={(term) => `Add As New Customer${term.trim() ? `: "${term.trim().toUpperCase()}"` : ''}`}
        ariaLabel={ariaLabel}
        placeholder={placeholder ?? 'Search customers — min 2 characters…'}
        searching={searching}
        emptyMessage={value.trim().length < 2 ? 'Type at least 2 characters to search' : undefined}
        renderOption={(opt) => {
          const r = opt.data as CustomerLookupRow | undefined;
          if (!r) return null;
          // Customers carry no SAGE/Terms (bill-to-level) — show rep + market.
          return (
            <LookupResultCard
              name={r.custname} code={r.customercode}
              address1={r.address1} address2={r.address2}
              city={r.city} state={r.state} zip={r.zip}
              details={[
                { label: 'Sales Rep', value: r.salesrep },
                { label: 'Market', value: r.market },
                { label: 'Key Acct', value: r.keyAccount },
              ]}
            />
          );
        }}
        className="w-full"
      />
    </div>
  );
};

export default CustomerLookupField;
